import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, ArrowLeft, ArrowRight, CheckCircle, Home, Calendar, FileText, MessageSquare, Clock, AlertTriangle, Lightbulb, Copy, Download, Heart, Lock, Phone, Mail } from "lucide-react";
import { jsPDF } from "jspdf";
import AnonymityBanner from "@/components/AnonymityBanner";
import NextStepsFooter from "@/components/NextStepsFooter";
import "@/styles/govtech.css";

// Logic Tag for B2B reporting
const MODULE_TAG = "Financial Defense Module";

const TenuresLogo = () => (
  <span className="text-xl font-bold text-[#333333]">
    Ten<span className="text-[#00703C] font-extrabold">UR</span>es<span className="text-[#00703C]">.co.uk</span>
  </span>
);

// Calculate months since tenancy start
const calculateTenancyMonths = (startDate) => {
  if (!startDate) return null;
  const start = new Date(startDate);
  const now = new Date();
  const months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  return Math.max(0, months);
};

export default function ArrearsSupport() {
  const navigate = useNavigate();
  
  // Triage state
  const [triagePath, setTriagePath] = useState(null);
  const [step, setStep] = useState('triage');
  
  // Talk Early form state
  const [talkEarlyForm, setTalkEarlyForm] = useState({
    name: "",
    reason: "",
    officerName: ""
  });
  const [templateCopied, setTemplateCopied] = useState(false);
  
  // Milestone calculator state
  const [tenancyStart, setTenancyStart] = useState("");
  const [milestoneCalculated, setMilestoneCalculated] = useState(false);
  
  // Evidence checklist state
  const [checklist, setChecklist] = useState({
    payslips: false,
    bankBalance: false,
    billsList: false,
    benefitLetters: false,
    tenancyAgreement: false
  });

  const worryReasons = [
    { id: "bill_shock", label: "Unexpected bill (car repair, appliance, etc.)" },
    { id: "benefit_delay", label: "Benefit payment delayed or stopped" },
    { id: "hours_cut", label: "Work hours reduced or job loss" },
    { id: "health", label: "Health issue affecting income" },
    { id: "family", label: "Family situation changed" },
    { id: "other", label: "Something else" }
  ];

  const generateEmailTemplate = () => {
    const reasonText = worryReasons.find(r => r.id === talkEarlyForm.reason)?.label || "a change in my circumstances";
    return `Subject: Quick chat about my rent - ${talkEarlyForm.name || '[Your Name]'}

Hi ${talkEarlyForm.officerName || '[Housing Officer Name]'},

I hope you're well. I'm getting in touch because I'm a bit worried about my rent this month.

The reason is: ${reasonText}.

I really value my home and want to keep things on track. I'd appreciate a quick chat about putting together a plan before things get difficult.

I'm being proactive because I know early communication helps everyone. Please let me know a good time to talk.

Thank you for your understanding.

Best wishes,
${talkEarlyForm.name || '[Your Name]'}`;
  };

  const handleCopyTemplate = () => {
    navigator.clipboard.writeText(generateEmailTemplate());
    setTemplateCopied(true);
    
    console.log(`[${MODULE_TAG}] Early-Intervention Nudge generated`, {
      reason: talkEarlyForm.reason,
      timestamp: new Date().toISOString()
    });
    
    const nudges = JSON.parse(localStorage.getItem('tenures_early_nudges') || '[]');
    nudges.push({
      type: 'talk_early',
      reason: talkEarlyForm.reason,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('tenures_early_nudges', JSON.stringify(nudges));
    
    setTimeout(() => setTemplateCopied(false), 3000);
  };

  const handleTriageSelect = (path) => {
    setTriagePath(path);
    console.log(`[${MODULE_TAG}] Triage path selected: ${path}`);
    
    if (path === 'healthy') {
      setStep('milestone');
    } else if (path === 'worried') {
      setStep('talk-early');
    } else if (path === 'behind') {
      setStep('evidence');
    }
  };

  const tenancyMonths = calculateTenancyMonths(tenancyStart);
  const monthsToSecure = tenancyMonths !== null ? Math.max(0, 12 - tenancyMonths) : null;
  const isPermanent = tenancyMonths !== null && tenancyMonths >= 12;

  const generateChecklistPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    doc.setFillColor(0, 112, 60);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.text("TenURes - Rent Discussion Checklist", 20, 25);
    
    doc.setTextColor(51, 51, 51);
    doc.setFontSize(12);
    
    let y = 55;
    
    doc.setFontSize(14);
    doc.text("Before You Call: Your Evidence Checklist", 20, y);
    y += 15;
    
    doc.setFontSize(11);
    const items = [
      { label: "Last 2 payslips (or benefit statements)", checked: checklist.payslips },
      { label: "Recent bank statement or balance screenshot", checked: checklist.bankBalance },
      { label: "List of essential bills (Electric, Gas, Council Tax)", checked: checklist.billsList },
      { label: "Any benefit award letters", checked: checklist.benefitLetters },
      { label: "Copy of your tenancy agreement", checked: checklist.tenancyAgreement }
    ];
    
    items.forEach(item => {
      doc.text(`${item.checked ? '☑' : '☐'} ${item.label}`, 25, y);
      y += 10;
    });
    
    y += 10;
    doc.setFontSize(12);
    doc.text("Why This Helps:", 20, y);
    y += 8;
    doc.setFontSize(10);
    doc.text("Having these documents ready shows your Housing Officer that you're", 20, y);
    y += 6;
    doc.text("being proactive and organised. This makes it much easier to agree a plan.", 20, y);
    
    y += 20;
    doc.setFontSize(9);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated by TenURes.co.uk | ${new Date().toLocaleDateString('en-GB')}`, 20, y);
    doc.text("Built with a decade of frontline housing expertise", 20, y + 5);
    
    doc.save("TenURes_Rent_Discussion_Checklist.pdf");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/resident')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft size={20} />
            <span>Back to Portal</span>
          </button>
          <TenuresLogo />
        </div>
      </nav>

      {/* Hero Section - Dark Header */}
      <section className="bg-[#333333] text-white py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="w-16 h-16 rounded-full bg-[#00703C] flex items-center justify-center mx-auto mb-6">
            <Home className="text-white" size={32} />
          </div>
          <p className="text-[#00703C] font-medium text-sm uppercase tracking-wide mb-2">Rent Support & Advice</p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Stay Secure in Your Home
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-6">
            Worried about rent? You're not alone. Use our free tools to take control, get prepared, and protect your tenancy.
          </p>
          
          {/* No-Fear Banner */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white bg-opacity-10 rounded-full">
            <Lock className="text-[#00703C]" size={16} />
            <span className="text-sm">
              <strong>100% Anonymous.</strong> We don't store your data or report to your landlord.
            </span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        
        {/* STEP 1: Tenancy Health Check Triage */}
        {step === 'triage' && (
          <section>
            {/* Section Header */}
            <div className="bg-white border border-gray-200 p-6 mb-6">
              <div className="text-center">
                <span className="inline-block px-3 py-1 bg-[#00703C] bg-opacity-10 text-[#00703C] text-sm font-medium mb-4">
                  Step 1 of 2
                </span>
                <h2 className="text-2xl font-bold text-[#333333] mb-2">Tenancy Health Check</h2>
                <p className="text-gray-600">Select the option that best describes your situation right now</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              {/* Healthy Path */}
              <button
                onClick={() => handleTriageSelect('healthy')}
                className="bg-white p-6 border-2 border-gray-200 hover:border-[#00703C] hover:shadow-lg transition-all text-left group"
              >
                <div className="w-14 h-14 rounded-full bg-[#00703C] flex items-center justify-center mb-4">
                  <CheckCircle className="text-white" size={28} />
                </div>
                <h3 className="font-bold text-[#333333] mb-2 text-lg">My rent is up to date</h3>
                <p className="text-sm text-gray-600 mb-4">Great! Let's make sure you stay on track for your permanent tenancy.</p>
                <span className="text-[#00703C] font-medium text-sm flex items-center">
                  Check my milestone <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
              
              {/* Worried Path */}
              <button
                onClick={() => handleTriageSelect('worried')}
                className="bg-white p-6 border-2 border-gray-200 hover:border-[#F47738] hover:shadow-lg transition-all text-left group"
              >
                <div className="w-14 h-14 rounded-full bg-[#F47738] flex items-center justify-center mb-4">
                  <AlertTriangle className="text-white" size={28} />
                </div>
                <h3 className="font-bold text-[#333333] mb-2 text-lg">I'm worried about next month</h3>
                <p className="text-sm text-gray-600 mb-4">Let's get ahead of this together. Early action makes all the difference.</p>
                <span className="text-[#F47738] font-medium text-sm flex items-center">
                  Get help now <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
              
              {/* Behind Path */}
              <button
                onClick={() => handleTriageSelect('behind')}
                className="bg-white p-6 border-2 border-gray-200 hover:border-[#D4351C] hover:shadow-lg transition-all text-left group"
              >
                <div className="w-14 h-14 rounded-full bg-[#D4351C] flex items-center justify-center mb-4">
                  <Clock className="text-white" size={28} />
                </div>
                <h3 className="font-bold text-[#333333] mb-2 text-lg">I've already missed a payment</h3>
                <p className="text-sm text-gray-600 mb-4">Don't worry - there's still time to sort this out. Let's get you prepared.</p>
                <span className="text-[#D4351C] font-medium text-sm flex items-center">
                  Get prepared <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
            </div>
            
            {/* Reassurance Card */}
            <div className="bg-[#00703C] text-white p-6 mb-8">
              <div className="flex items-start gap-4">
                <Heart className="flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-bold mb-2">Whatever your situation, you're doing the right thing</h3>
                  <p className="text-green-100 text-sm">
                    Taking action today puts you back in control. Most rent issues are resolved when people reach out early. 
                    You're not alone - thousands of residents use these tools every month.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Did You Know Section */}
            <div className="bg-white border border-gray-200 p-6 mb-6">
              <h3 className="font-bold text-[#333333] mb-4 flex items-center gap-2">
                <Lightbulb className="text-[#F47738]" size={20} />
                Did You Know?
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-4 bg-[#F8F8F8] border-l-4 border-[#00703C]">
                  <p className="text-2xl font-bold text-[#00703C]">90%</p>
                  <p className="text-sm text-gray-600">of rent issues are resolved when residents reach out early</p>
                </div>
                <div className="p-4 bg-[#F8F8F8] border-l-4 border-[#00703C]">
                  <p className="text-2xl font-bold text-[#00703C]">£12,000</p>
                  <p className="text-sm text-gray-600">average cost of an eviction to councils - they want to avoid it too</p>
                </div>
                <div className="p-4 bg-[#F8F8F8] border-l-4 border-[#F47738]">
                  <p className="text-2xl font-bold text-[#F47738]">3 weeks</p>
                  <p className="text-sm text-gray-600">earlier intervention happens when residents use tools like this</p>
                </div>
                <div className="p-4 bg-[#F8F8F8] border-l-4 border-[#F47738]">
                  <p className="text-2xl font-bold text-[#F47738]">12 months</p>
                  <p className="text-sm text-gray-600">of clear rent = permanent tenancy security</p>
                </div>
              </div>
            </div>
            
            {/* What Happens If I Don't Act - Timeline */}
            <div className="bg-white border border-gray-200 p-6 mb-6">
              <h3 className="font-bold text-[#333333] mb-4">What Happens If Rent Falls Behind?</h3>
              <p className="text-sm text-gray-600 mb-6">Understanding the process helps you stay ahead. Here's what typically happens:</p>
              
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                
                <div className="space-y-6">
                  <div className="flex gap-4 relative">
                    <div className="w-8 h-8 rounded-full bg-[#00703C] flex items-center justify-center z-10 flex-shrink-0">
                      <span className="text-white text-sm font-bold">1</span>
                    </div>
                    <div className="flex-1 pb-2">
                      <p className="font-medium text-[#333333]">Week 1-2: First reminder letter</p>
                      <p className="text-sm text-gray-500">A friendly reminder that rent is due. Easy to resolve at this stage.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 relative">
                    <div className="w-8 h-8 rounded-full bg-[#F47738] flex items-center justify-center z-10 flex-shrink-0">
                      <span className="text-white text-sm font-bold">2</span>
                    </div>
                    <div className="flex-1 pb-2">
                      <p className="font-medium text-[#333333]">Week 3-4: Second notice + contact attempt</p>
                      <p className="text-sm text-gray-500">Housing Officer tries to reach you. Responding now prevents escalation.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 relative">
                    <div className="w-8 h-8 rounded-full bg-[#D4351C] flex items-center justify-center z-10 flex-shrink-0">
                      <span className="text-white text-sm font-bold">3</span>
                    </div>
                    <div className="flex-1 pb-2">
                      <p className="font-medium text-[#333333]">Week 5-8: Formal warning (NOSP)</p>
                      <p className="text-sm text-gray-500">Notice of Seeking Possession. Still time to agree a payment plan.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 relative">
                    <div className="w-8 h-8 rounded-full bg-[#333333] flex items-center justify-center z-10 flex-shrink-0">
                      <span className="text-white text-sm font-bold">4</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-[#333333]">Month 3+: Court proceedings</p>
                      <p className="text-sm text-gray-500">Legal action begins. Much harder (but not impossible) to stop.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-[#00703C] bg-opacity-10 border border-[#00703C] border-opacity-30">
                <p className="text-sm text-[#00703C] font-medium">
                  <CheckCircle className="inline mr-2" size={16} />
                  The earlier you act, the more options you have. Use our tools above to get started today.
                </p>
              </div>
            </div>
            
            {/* Other Tools */}
            <div className="bg-[#333333] text-white p-6">
              <h3 className="font-bold mb-4">Other Tools That Might Help</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                <button 
                  onClick={() => navigate('/succession-triage')}
                  className="p-4 bg-white bg-opacity-10 hover:bg-opacity-20 transition-all text-left"
                >
                  <Shield className="mb-2 text-[#00703C]" size={24} />
                  <p className="font-medium">Succession Rights</p>
                  <p className="text-xs text-gray-400">Check your right to stay</p>
                </button>
                <button 
                  onClick={() => navigate('/rtb-triage')}
                  className="p-4 bg-white bg-opacity-10 hover:bg-opacity-20 transition-all text-left"
                >
                  <Home className="mb-2 text-[#00703C]" size={24} />
                  <p className="font-medium">Right to Buy</p>
                  <p className="text-xs text-gray-400">Calculate your discount</p>
                </button>
                <button 
                  onClick={() => navigate('/milestone-guardian')}
                  className="p-4 bg-white bg-opacity-10 hover:bg-opacity-20 transition-all text-left"
                >
                  <Calendar className="mb-2 text-[#00703C]" size={24} />
                  <p className="font-medium">Milestone Guardian</p>
                  <p className="text-xs text-gray-400">Track important dates</p>
                </button>
              </div>
            </div>
          </section>
        )}

        {/* STEP 2A: Talk Early Helper */}
        {step === 'talk-early' && (
          <section>
            <button 
              onClick={() => setStep('triage')} 
              className="flex items-center gap-2 text-[#00703C] mb-6 hover:underline font-medium"
            >
              <ArrowLeft size={18} /> Back to Health Check
            </button>
            
            {/* Header Card */}
            <div className="bg-[#F47738] text-white p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
                  <MessageSquare size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold mb-2">The "Talk Early" Helper</h2>
                  <p className="text-orange-100">
                    The single best thing you can do is reach out to your Housing Officer <strong>before</strong> you miss a payment. 
                    This simple step can prevent 90% of rent problems from escalating.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Form Card */}
            <div className="bg-white border border-gray-200 p-6 mb-6">
              <h3 className="font-bold text-[#333333] mb-4 text-lg">Create Your Contact Message</h3>
              
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-1">Your first name</label>
                  <input
                    type="text"
                    className="govtech-input"
                    placeholder="e.g., Sarah"
                    value={talkEarlyForm.name}
                    onChange={(e) => setTalkEarlyForm({...talkEarlyForm, name: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-1">Housing Officer's name (if you know it)</label>
                  <input
                    type="text"
                    className="govtech-input"
                    placeholder="e.g., Mr. Johnson"
                    value={talkEarlyForm.officerName}
                    onChange={(e) => setTalkEarlyForm({...talkEarlyForm, officerName: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-2">What's causing the worry?</label>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {worryReasons.map(reason => (
                      <button
                        key={reason.id}
                        onClick={() => setTalkEarlyForm({...talkEarlyForm, reason: reason.id})}
                        className={`p-3 text-left text-sm border-2 transition-all ${
                          talkEarlyForm.reason === reason.id
                            ? 'border-[#00703C] bg-[#00703C] bg-opacity-5 text-[#00703C] font-medium'
                            : 'border-gray-200 text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {reason.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Generated Template */}
              {talkEarlyForm.reason && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <label className="block text-sm font-medium text-[#333333] mb-2">Your Ready-to-Send Message:</label>
                  <div className="bg-[#F8F8F8] p-4 font-mono text-sm whitespace-pre-wrap border border-gray-200 max-h-64 overflow-y-auto">
                    {generateEmailTemplate()}
                  </div>
                  
                  <button
                    onClick={handleCopyTemplate}
                    className={`mt-4 w-full govtech-btn ${templateCopied ? 'bg-[#00703C]' : 'govtech-btn-primary'}`}
                  >
                    <Copy size={18} className="mr-2" />
                    {templateCopied ? 'Copied to Clipboard!' : 'Copy Message to Clipboard'}
                  </button>
                  
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    Paste this into an email to your Housing Officer, or read it out if you call them.
                  </p>
                </div>
              )}
            </div>
            
            {/* Why This Works */}
            <div className="bg-[#333333] text-white p-6 mb-6">
              <h3 className="font-bold mb-3 flex items-center gap-2">
                <Lightbulb className="text-[#00703C]" size={20} />
                Why This Works
              </h3>
              <p className="text-gray-300 text-sm mb-3">
                Housing Officers deal with difficult situations every day. When someone reaches out <em>before</em> things get serious, 
                it makes their job easier and shows you're taking responsibility.
              </p>
              <div className="flex items-center gap-2 text-[#00703C] font-medium">
                <CheckCircle size={16} />
                <span className="text-sm">Residents who contact early are 3x more likely to keep their tenancy</span>
              </div>
            </div>
            
            {/* Navigation */}
            <div className="grid sm:grid-cols-2 gap-4">
              <button 
                onClick={() => setStep('evidence')} 
                className="govtech-btn govtech-btn-secondary"
              >
                Get My Evidence Checklist <ArrowRight size={18} className="ml-2" />
              </button>
              <button 
                onClick={() => setStep('milestone')} 
                className="govtech-btn govtech-btn-secondary"
              >
                Check My Tenancy Milestone <ArrowRight size={18} className="ml-2" />
              </button>
            </div>
          </section>
        )}

        {/* STEP 2B: Evidence Checklist */}
        {step === 'evidence' && (
          <section>
            <button 
              onClick={() => setStep('triage')} 
              className="flex items-center gap-2 text-[#00703C] mb-6 hover:underline font-medium"
            >
              <ArrowLeft size={18} /> Back to Health Check
            </button>
            
            {/* Header Card */}
            <div className="bg-[#D4351C] text-white p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
                  <FileText size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold mb-2">Be "Officer-Ready" Before You Call</h2>
                  <p className="text-red-100">
                    Having the right documents ready shows you're serious about sorting this out. 
                    It helps your Housing Officer understand your situation quickly.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Checklist Card */}
            <div className="bg-white border border-gray-200 p-6 mb-6">
              <h3 className="font-bold text-[#333333] mb-2 text-lg">Your Evidence Checklist</h3>
              <p className="text-sm text-gray-600 mb-6">Tick each item as you gather it. You don't need everything, but the more you have, the better.</p>
              
              <div className="space-y-3 mb-6">
                {[
                  { key: 'payslips', label: 'Last 2 payslips or benefit statements', hint: 'Shows your current income' },
                  { key: 'bankBalance', label: 'Recent bank statement or balance screenshot', hint: 'Shows your financial situation' },
                  { key: 'billsList', label: 'List of essential bills (Electric, Gas, Council Tax)', hint: 'Shows your outgoings' },
                  { key: 'benefitLetters', label: 'Any benefit award letters', hint: 'Proves what support you receive' },
                  { key: 'tenancyAgreement', label: 'Copy of your tenancy agreement', hint: 'Good to have for reference' }
                ].map(item => (
                  <label 
                    key={item.key}
                    className={`flex items-start gap-3 p-4 border-2 cursor-pointer transition-all ${
                      checklist[item.key] 
                        ? 'border-[#00703C] bg-[#00703C] bg-opacity-5' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checklist[item.key]}
                      onChange={(e) => setChecklist({...checklist, [item.key]: e.target.checked})}
                      className="mt-1 w-5 h-5 text-[#00703C] rounded"
                    />
                    <div>
                      <p className={`font-medium ${checklist[item.key] ? 'text-[#00703C]' : 'text-[#333333]'}`}>
                        {item.label}
                      </p>
                      <p className="text-xs text-gray-500">{item.hint}</p>
                    </div>
                    {checklist[item.key] && (
                      <CheckCircle className="ml-auto text-[#00703C] flex-shrink-0" size={20} />
                    )}
                  </label>
                ))}
              </div>
              
              <button
                onClick={generateChecklistPDF}
                className="govtech-btn govtech-btn-primary w-full"
              >
                <Download size={18} className="mr-2" />
                Download My Checklist as PDF
              </button>
            </div>

            {/* Next Steps / Call-Deflation */}
            <NextStepsFooter reviewDays={14} teamName="housing team" />
            
            {/* What to Say Card */}
            <div className="bg-[#333333] text-white p-6 mb-6">
              <h3 className="font-bold mb-3 flex items-center gap-2">
                <Phone size={20} className="text-[#00703C]" />
                What to Say When You Call
              </h3>
              <div className="bg-white bg-opacity-10 p-4 text-sm italic mb-4">
                "Hi, I'm calling about my rent account. I know I've fallen behind and I want to sort it out. 
                I've got my documents ready and I'd like to discuss a payment plan that works for both of us."
              </div>
              <p className="text-xs text-gray-400">
                This simple opening shows you're taking responsibility and ready to work together on a solution.
              </p>
            </div>
            
            {/* Support Links */}
            <div className="bg-white border border-gray-200 p-6 mb-6">
              <h3 className="font-bold text-[#333333] mb-4">Extra Support Available</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { name: "Citizens Advice", desc: "Free, confidential debt advice", url: "https://www.citizensadvice.org.uk/debt-and-money/rent-arrears/" },
                  { name: "StepChange", desc: "Free debt management plans", url: "https://www.stepchange.org/" },
                  { name: "Shelter", desc: "Housing and homelessness charity", url: "https://www.shelter.org.uk/housing_advice/paying_for_a_home/rent_arrears" },
                  { name: "DHP (Council Help)", desc: "Extra help with housing costs", url: "https://www.gov.uk/discretionary-housing-payments" }
                ].map(link => (
                  <a 
                    key={link.name}
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-4 border-2 border-gray-200 hover:border-[#00703C] hover:bg-[#00703C] hover:bg-opacity-5 transition-all flex items-center gap-3"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center flex-shrink-0">
                      <ArrowRight className="text-[#00703C]" size={18} />
                    </div>
                    <div>
                      <p className="font-medium text-[#333333]">{link.name}</p>
                      <p className="text-xs text-gray-500">{link.desc}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
            
            {/* CTA */}
            <button 
              onClick={() => setStep('talk-early')} 
              className="govtech-btn govtech-btn-primary w-full"
            >
              Generate a Contact Message for My Housing Officer <ArrowRight size={18} className="ml-2" />
            </button>
          </section>
        )}

        {/* STEP 2C: Milestone Calculator */}
        {step === 'milestone' && (
          <section>
            <button 
              onClick={() => setStep('triage')} 
              className="flex items-center gap-2 text-[#00703C] mb-6 hover:underline font-medium"
            >
              <ArrowLeft size={18} /> Back to Health Check
            </button>
            
            {/* Header Card */}
            <div className="bg-[#00703C] text-white p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
                  <Calendar size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold mb-2">Your Path to Permanent Security</h2>
                  <p className="text-green-100">
                    <strong>Did you know?</strong> Keeping your rent clear is the #1 way to move from a 'Starter' tenancy to a 'Permanent' (Assured) one.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Calculator Card */}
            <div className="bg-white border border-gray-200 p-6 mb-6">
              <h3 className="font-bold text-[#333333] mb-4 text-lg">Tenancy Milestone Calculator</h3>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#333333] mb-2">When did your tenancy start?</label>
                <input
                  type="date"
                  className="govtech-input"
                  value={tenancyStart}
                  onChange={(e) => {
                    setTenancyStart(e.target.value);
                    setMilestoneCalculated(true);
                  }}
                />
              </div>
              
              {milestoneCalculated && tenancyMonths !== null && (
                <div className="mt-6">
                  {isPermanent ? (
                    <div className="bg-[#00703C] text-white p-6 text-center">
                      <CheckCircle className="mx-auto mb-4" size={48} />
                      <h3 className="text-2xl font-bold mb-2">Congratulations!</h3>
                      <p className="text-green-100 mb-4">
                        You've been in your home for <strong>{tenancyMonths} months</strong>. 
                        You should now have a <strong>Permanent (Assured) Tenancy</strong>.
                      </p>
                      <p className="text-green-200 text-sm">
                        This means you have much stronger rights to stay in your home. Keep your rent on track and this security is yours.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-[#F8F8F8] border-2 border-[#F47738] p-6 text-center">
                      <div className="w-16 h-16 rounded-full bg-[#F47738] flex items-center justify-center mx-auto mb-4">
                        <Clock className="text-white" size={32} />
                      </div>
                      <h3 className="text-3xl font-bold text-[#333333] mb-2">
                        {monthsToSecure} months to go!
                      </h3>
                      <p className="text-gray-600 mb-6">
                        You've been in your home for <strong>{tenancyMonths} months</strong>. 
                        You're just <strong>{monthsToSecure} months away</strong> from permanent security.
                      </p>
                      
                      {/* Progress bar */}
                      <div className="mb-4">
                        <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#00703C] transition-all duration-500"
                            style={{ width: `${Math.min(100, (tenancyMonths / 12) * 100)}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-2">
                          <span>Start</span>
                          <span className="font-medium text-[#00703C]">12 months = Permanent</span>
                        </div>
                      </div>
                      
                      <p className="text-[#F47738] font-medium">
                        Stay on track! Keeping your rent clear during this period is crucial.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Reminder CTA */}
            <div className="bg-[#333333] text-white p-6">
              <h3 className="font-bold mb-3">Want a Reminder?</h3>
              <p className="text-gray-300 text-sm mb-4">
                Use our Milestone Guardian to set up an SMS or WhatsApp reminder for when you reach your permanent tenancy date.
              </p>
              <button 
                onClick={() => navigate('/milestone-guardian')}
                className="govtech-btn bg-[#00703C] hover:bg-[#005a30] text-white w-full sm:w-auto"
              >
                Set Up My Reminder <ArrowRight size={18} className="ml-2" />
              </button>
            </div>
          </section>
        )}

      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <TenuresLogo />
          <p className="text-sm text-gray-500 mt-4">
            Built with a decade of frontline housing expertise helping residents avoid eviction.
          </p>
          <p className="text-xs text-gray-400 mt-2">
            This is guidance only, not legal advice. Always speak to your Housing Officer about your specific situation.
          </p>
        </div>
      </footer>
    </div>
  );
}
